import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone,
  ShieldCheck,
  MessageSquare,
  Copy,
  Settings as SettingsIcon,
  Image as ImageIcon,
  MapPin,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { INACTIVE_NOTE, SPECIAL_NOTE, formatPhone, locationOf, numberBadges } from '../../../lib/phoneLifecycle.js';
import usePhoneLifecycle from '../hooks/usePhoneLifecycle.js';
import NumberActionsMenu from './NumberActionsMenu.jsx';
import NumberFilterBar from './NumberFilterBar.jsx';
import { DEFAULT_FILTERS, applyFilters, clearAll } from '../../../lib/numberFilters.js';
import NumberDialogs from './NumberDialogs.jsx';
import PendingDeletionSection from './PendingDeletionSection.jsx';

/**
 * "Active Numbers · My Phone Number": the agent's own lines with a More menu
 * (default / inactive / special / detach / delete) and the Pending Deletion list beneath.
 * @param {{ owned: Object[] }} props - Phone number records from GET /api/phones.
 */
export default function ActiveNumbersTable({ owned }) {
  const navigate = useNavigate();
  const life = usePhoneLifecycle();
  const [dialog, setDialog] = useState(null); // {kind, number}
  // Filters live here (not in the data), so a row action that refreshes the list never resets them.
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const visible = useMemo(() => applyFilters(owned, filters), [owned, filters]);
  const close = () => setDialog(null);

  async function copyNumber(raw) {
    try {
      await navigator.clipboard.writeText(String(raw));
      toast.success('Number copied');
    } catch {
      toast.error('Could not copy number');
    }
  }

  function onAction(action, n) {
    if (action === 'default') life.setDefault.mutate(n._id);
    else if (action === 'status') life.setStatus.mutate({ id: n._id, status: n.status === 'inactive' ? 'active' : 'inactive' });
    else if (action === 'special') life.setSpecial.mutate({ id: n._id, isSpecial: !n.isSpecial });
    else if (action === 'detach') setDialog({ kind: 'detach', number: n });
    else if (action === 'delete') setDialog({ kind: 'ask', number: n });
  }

  function onConfirm(kind) {
    const id = dialog.number._id;
    const done = { onSettled: close };
    if (kind === 'detach') life.detach.mutate(id, done);
    else if (kind === 'extend') life.extend.mutate(id, done);
    else if (kind === 'retain') life.remove.mutate({ id, retain30Days: true }, done);
    else life.remove.mutate({ id, retain30Days: false }, done); // 'immediate' | 'permanent'
  }

  return (
    <>
      <div className="panel p-5 space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="font-display font-bold">Active Numbers</div>
            <span className="text-white/30">·</span>
            <span className="px-2 py-0.5 rounded-full bg-civix-purple/15 text-civix-purpleSoft border border-civix-purple/30 text-[11px] font-semibold">My Phone Number</span>
          </div>
          {owned.length > 0 && (
            <span className="text-xs text-white/50">{owned.length} line{owned.length === 1 ? '' : 's'}</span>
          )}
        </div>

        {owned.length > 0 && <NumberFilterBar numbers={owned} filters={filters} onChange={setFilters} shown={visible.length} />}

        {!owned.length ? (
          <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] py-8 px-4 text-center">
            <Phone className="w-6 h-6 mx-auto mb-2 text-white/30" />
            <div className="text-sm text-white/50">
              No active phone numbers yet. Search and activate your first line above.
            </div>
          </div>
        ) : visible.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] py-8 px-4 text-center" data-testid="no-number-matches">
            <Phone className="w-6 h-6 mx-auto mb-2 text-white/30" />
            <div className="text-sm text-white/60">No phone numbers match your filter criteria.</div>
            <button type="button" onClick={() => setFilters(clearAll(filters))} className="mt-3 px-3 py-1.5 rounded-lg bg-civix-purple text-white text-xs font-semibold hover:bg-civix-purple/90" data-testid="clear-filters">
              Clear Filters
            </button>
          </div>
        ) : (
          <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Rows are keyed and acted on by _id, so filtering/sorting never shifts which number an action hits. */}
            {visible.map((n) => (
              <li
                key={n._id || n.number}
                className={`rounded-xl border border-white/10 bg-[#140b22]/70 backdrop-blur p-4 space-y-3 hover:border-civix-purple/40 transition ${n.status === 'inactive' ? 'opacity-90' : ''}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-lg font-semibold text-white">{formatPhone(n.number)}</span>
                      <button
                        type="button"
                        onClick={() => copyNumber(n.number)}
                        className="p-1 rounded text-white/40 hover:text-civix-cyan hover:bg-white/5 transition"
                        title="Copy number"
                        aria-label="Copy number"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-white/60 mt-0.5">
                      <MapPin className="w-3 h-3" /> {locationOf(n)}
                      {n.areaCode ? <span className="text-white/30">· {n.areaCode}</span> : null}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {numberBadges(n).map((b) => (
                      <span key={b.key} data-testid={`badge-${b.key}`} title={b.key === 'inactive' ? INACTIVE_NOTE : b.key === 'special' ? SPECIAL_NOTE : undefined} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${b.cls}`}>
                        {b.label}
                      </span>
                    ))}
                  </div>
                </div>

                {n.status === 'inactive' && <p className="text-[11px] text-amber-200/80 leading-snug">{INACTIVE_NOTE}</p>}

                <div className="flex flex-wrap items-center gap-1.5">
                  {n.capabilities?.voice !== false && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-civix-purple/15 text-civix-purpleSoft border border-civix-purple/30">
                      <Phone className="w-3 h-3" /> Voice
                    </span>
                  )}
                  {n.capabilities?.sms !== false && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-civix-cyan/10 text-civix-cyan border border-civix-cyan/30">
                      <MessageSquare className="w-3 h-3" /> SMS
                    </span>
                  )}
                  {n.capabilities?.mms && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-white/5 text-white/70 border border-white/15">
                      <ImageIcon className="w-3 h-3" /> MMS
                    </span>
                  )}
                  {n.a2pCampaign && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-emerald-500/10 text-emerald-300 border border-emerald-500/25">
                      <ShieldCheck className="w-3 h-3" /> A2P Registered
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between gap-2 pt-3 border-t border-white/10 text-xs">
                  <span className="text-white/50">
                    $1.00/mo billed to Telecom Wallet
                    {n.createdAt ? ` · since ${new Date(n.createdAt).toLocaleDateString()}` : ''}
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => navigate('/settings?section=phone')}
                      className="inline-flex items-center gap-1 text-civix-cyan hover:text-white transition font-medium"
                    >
                      <SettingsIcon className="w-3.5 h-3.5" /> Configure
                    </button>
                    <NumberActionsMenu number={n} onAction={(a) => onAction(a, n)} />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <PendingDeletionSection
        numbers={life.pending}
        busy={life.busy}
        onRestore={(n) => life.restore.mutate(n._id)}
        onExtend={(n) => setDialog({ kind: 'extend', number: n })}
        onDeletePermanently={(n) => setDialog({ kind: 'permanent', number: n })}
      />

      <NumberDialogs dialog={dialog} busy={life.busy} onClose={close} onGo={(kind) => setDialog({ kind, number: dialog.number })} onConfirm={onConfirm} />
    </>
  );
}
